import React, { Component, ReactNode } from 'react';
import { RootState } from '../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { changeActivePageIndex, changeCurrentCategory, changeEnrichedCategories, changePagePath, nextPage, previousPage } from '../redux/actions/PageActions';
import { FormicaProps, withFormica } from '../helpers/withFormica';
import { PagePath, PageState } from '../redux/reducers/PageReducer';
import FolderView from './folder/view/FolderView';
import Icon from '../icons/Icon';

class Breadcrumb extends Component<Props> {
  public render(): ReactNode {
    const {
      page: { pagePath, activePageIndex, currentComponent },
      views,
    } = this.props;
    const canGoPrevious = activePageIndex - 1 >= 0 && pagePath.length > activePageIndex;
    const canGoNext = pagePath.length > activePageIndex + 1;
    return (
      <div className={'flex select-none items-center px-5 border-[1px] border-l-0 border-[#E8E8E8] bg-white w-full font-formica transition-all'}>
        <div className='flex flex-row fill-f-black-2 mr-5'>
          <span
            className={`${canGoPrevious ? 'cursor-pointer opacity-100' : 'cursor-not-allowed opacity-20'} transition-opacity`}
            onClick={this.goPrevious}
            data-testid={`${currentComponent}_breadcrumb_previous_button`}
          >
            <Icon icon={'arrow-left'} className={'text-f-black-2'} size={20} />
          </span>
          <span
            className={`${canGoNext ? 'cursor-pointer opacity-100' : 'cursor-not-allowed opacity-20'} transition-opacity`}
            onClick={this.goNext}
            data-testid={`${currentComponent}_breadcrumb_next_button`}
          >
            <Icon icon={'arrow-right'} className={'text-black-2'} size={20} />
          </span>
        </div>
        <div aria-label='breadcrumb' className='flex h-[48px] 2xl:h-[54px] gap-x-2 w-full items-center overflow-x-scroll'>
          {pagePath.slice(0, activePageIndex + 1).map((p: PagePath, index: number) => {
            if (index == activePageIndex) {
              return p?.payload ? (
                <div className={'flex shrink-0'} key={p.payload.id + index} id={`parent_breadcrumb_${p.payload.id}`}>
                  <span
                    id={`breadcrumb_${p.payload.id}`}
                    className='text-f-black-2 gap-x-1 border-[1px] border-transparent px-2 py-1 rounded-[4px]  hover:bg-[#EDEDED] hover:text-f-black-2 hover:cursor-pointer text-sm flex shrink-0 items-center'
                  >
                    {this.getIcon(p)}
                    <p className='breadcrumb-active-page-title'>{p.title}</p>
                  </span>
                </div>
              ) : null;
            }
            return p?.payload ? (
              <button
                className={'flex items-center gap-x-1 shrink-0 opacity-30  hover:opacity-100'}
                key={p.payload.id + index}
                onClick={() => this.go(p, index)}
              >
                <section className={'flex items-center gap-x-1'}>
                  <div className={'flex shrink-0'} id={`parent_breadcrumb_${p.payload.id}`}>
                    <span
                      id={`breadcrumb_${p.payload.id}`}
                      className='text-f-black-2 gap-x-1 border-[1px]  border-transparent px-2 py-1 rounded-[4px]  hover:bg-[#EDEDED] hover:text-f-black-2 hover:cursor-pointer text-sm flex shrink-0 items-center'
                    >
                      {this.getIcon(p)}
                      <p className='breadcrumb-page-title'>{p.title}</p>
                    </span>
                  </div>
                </section>
                {index <= pagePath.length - 1 && <span className='text-f-black-3'>/</span>}
              </button>
            ) : null;
          })}
        </div>
      </div>
    );
  }

  private go = (page: PagePath, index: number) => {
    const {
      page: { enrichedCategories, pagePath, lastEnrichedCategories },
      changeCurrentCategory,
      changeActivePageIndex,
      changeEnrichedCategories,
      router: { navigate },
    } = this.props;
    changeActivePageIndex(index);
    if (pagePath && pagePath.length > 0) {
      const type = pagePath[pagePath.length - 1].type;
      if (type == 'componentId') {
        if (page.type == 'category' || page.type == 'componentId') {
          navigate(`/${page.path}`);
        }
      }
    }
    if (page.type == 'category') {
      if (Object.keys(enrichedCategories).length == 0) {
        changeEnrichedCategories(lastEnrichedCategories);
        const enrichedCategory = lastEnrichedCategories[page.categoryPath];
        setTimeout(() => {
          changeCurrentCategory(enrichedCategory);
          if (page.path) {
            navigate(`/${page.path}`);
          }
        }, 0);
      } else {
        const enrichedCategory = enrichedCategories[page.categoryPath];
        setTimeout(() => {
          if (page.path) {
            navigate(`/${page.path}`);
          }
          changeCurrentCategory(enrichedCategory);
        }, 0);
      }
    }
  };

  private goPrevious = () => {
    const {
      page: { activePageIndex, pagePath },
    } = this.props;
    const canGoPrevious = activePageIndex - 1 >= 0 && pagePath.length > activePageIndex;
    if (!canGoPrevious) return;
    const page = pagePath[activePageIndex - 1];
    this.go(page, activePageIndex - 1);
  };

  private goNext = () => {
    const {
      page: { activePageIndex, pagePath },
    } = this.props;
    const canGoNext = pagePath.length > activePageIndex + 1;
    if (!canGoNext) return;
    const page = pagePath[activePageIndex + 1];
    this.go(page, activePageIndex + 1);
  };

  private getIcon = (pagePath: PagePath) => {
    if (pagePath.icon) {
      return <span className=''>{pagePath.icon}</span>;
    }

    if (pagePath.type == 'category') {
      return (
        <span className='fill-[#4F4F4F]'>
          <Icon icon={'folder'} color={'#4F4F4F'} size={20} />
        </span>
      );
    }
    return null;
  };
}

interface Props extends ReduxProps, FormicaProps {
  views?: FolderView[] | undefined;
}

const mapStateToProps = (state: RootState) => ({
  page: state.page as PageState,
  component: state.component,
});
const mapDispatchToProps = { changePagePath, nextPage, previousPage, changeCurrentCategory, changeActivePageIndex, changeEnrichedCategories };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(Breadcrumb));
